
/** @type {WeakMap<object, SlotMap>} */
const SLOT_MAP_CONTAINER = new WeakMap()
const SYMBOL_IS_SLOT_MAP = Symbol.for('neumatter.SlotMap.isSlotMap')
const PROCESS_ID = Math.floor(Math.random() * 0xFFFFFFFF).toString(16)

export default class SlotMap extends Map {
  /**
   * Gets or creates the SlotMap for the given object.
   * @param {object} obj
   * @returns {SlotMap}
   */
  static get (obj) {
    let slotMap = SLOT_MAP_CONTAINER.get(obj)

    if (!slotMap) {
      slotMap = new SlotMap()
      SLOT_MAP_CONTAINER.set(obj, slotMap)
    }

    return slotMap
  }

  /**
   * Sets the SlotMap for the given object.
   * @param {object} obj
   * @param {SlotMap} slotMap
   * @returns {SlotMap}
   */
  static set (obj, slotMap) {
    return SLOT_MAP_CONTAINER.set(obj, slotMap)
  }

  /**
   * Deletes the SlotMap for the given object.
   * @param {object} obj
   * @returns {SlotMap}
   */
  static delete (obj) {
    return  SLOT_MAP_CONTAINER.delete(obj)
  }

  /**
   * Gets a property in the SlotMap for the given object.
   * @param {object} obj
   * @param {string} propertyName
   * @returns {any}
   */
  static getInternal (obj, propertyName) {
    return SlotMap.get(obj).get(propertyName)
  }

  /**
   * Sets a property in the SlotMap for the given object.
   * @param {object} obj
   * @param {string} propertyName
   * @param {any} value
   * @returns {void}
   */
  static setInternal (obj, propertyName, value) {
    SlotMap.get(obj).set(propertyName, value)
  }

  static isSlotMap (input) {
    return (
      typeof input === 'object' &&
      input !== null &&
      typeof input[SYMBOL_IS_SLOT_MAP] === 'string' &&
      input[SYMBOL_IS_SLOT_MAP] === PROCESS_ID
    )
  }

  #timestamp

  constructor (slotMapToClone) {
    super()
    this.#timestamp = globalThis.performance ? globalThis.performance.now() : Date.now()

    if (slotMapToClone !== undefined && SlotMap.isSlotMap(slotMapToClone)) {
      for (const entry of slotMapToClone.entries()) {
        super.set(...entry)
      }
    }
  }

  get timestamp () {
    return this.#timestamp
  }

  get [SYMBOL_IS_SLOT_MAP] () {
    return PROCESS_ID
  }

  clear () {
    super.clear()
  }

  /**
   *
   * @param {Array<string> | undefined} allowedFields
   * @returns {Record<string, any>}
   */
  createPublicRecord (allowedFields) {
    const internalSlots = {}

    if (allowedFields === undefined) {
      for (const [key, value] of this.entries()) {
        if (key !== 'internal') internalSlots[key] = value
      }
    } else if (Array.isArray(allowedFields) || allowedFields instanceof Set) {
      const allowedSet = Array.isArray(allowedFields) ? new Set(allowedFields) : allowedFields

      for (const [key, value] of this.entries()) {
        if (allowedSet.has(key)) {
          internalSlots[key] = value
        }
      }
    } else {
      throw new TypeError('allowedFields should be undefined or Array<string>')
    }

    Object.freeze(internalSlots)
    return internalSlots
  }

  /**
   *
   * @param {string} key
   * @returns {boolean} true if an element in the slotMap existed and has been removed, or false if the element does not exist.
   */
  delete (key) {
    return super.delete(String(key))
  }

  /**
   * Returns an iterable object yielding the entries in the slotMap.
   * In order from most recently used to least recently used.
   * @returns {IterableIterator<[string, any]>}
   */
  entries () {
    return super.entries()
  }

  /**
   * Returns the value by searching the slotMap.
   * @param {(value: any, key: string, cache: LRUCache) => any} findFunction Should return truthy value when item found.
   * @returns {any}
   */
  find (findFunction) {
    for (const [key, value] of super.entries()) {
      const isFound = findFunction(value, key, this)
      if (isFound) return value
    }
  }

  /**
   *
   * @param {(value: any, key: string) => void} callback
   */
  forEach (callback) {
    super.forEach((entry, key) => {
      callback(entry.value, key)
    })
  }

  /**
   * Returns the value by its key.
   * Time complexity: O(1) in average.
   * @param {string} key
   * @param {{ allowStale: boolean } | undefined} options
   * @returns {any}
   */
  get (key) {
    return super.get(String(key))
  }

  keys () {
    return super.keys()
  }

  /**
   * Returns the value by its key.
   * Does not update order of list.
   * Time complexity: O(1) in average.
   * @param {string} key
   * @returns {any}
   */
  peek (key) {
    return super.get(String(key))
  }

  /**
   * Sets the value to slotMap by its key.
   * Time complexity: O(1).
   * @param {string} key
   * @param {any} value
   */
  set (key, value) {
    super.set(String(key), value)
    return this
  }

  values () {
    return super.values()
  }
}
